import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';

import { useMount } from '@simplywallst/ui-core';

import { OneColumn } from '@/components/Layout';
import DocumentHeader from '@/components/DocumentHeader';
import { BrazeLogPortfolioVisit } from '@/components/BrazeCustomEvents';

import { usePortfolio } from '@/hooks/portfolio/usePortfolio';

import { CompanyNoteModalProvider } from '@/components/Note';
import { ManualPortfolioProvider } from '../../components/PortfolioManual';

import { usePortfolioLinkSettings } from '../../hooks/usePortfolioLinkSettings';
import { useReduxStateForPortfolio } from '../../hooks/useReduxStateForPortfolio';

const EmptyState = loadable(() => import('./components/EmptyState'));
const ErrorState = loadable(() => import('../../components/ErrorState'), {
  resolveComponent: (c) => c.ErrorState,
});
const ImportingState = loadable(() => import('./components/ImportingState'));
const PopulatedState = loadable(() => import('./components/PopulatedState'), {
  resolveComponent: (c) => c.PopulatedState,
});
const LinkingErrorState = loadable(
  () => import('./components/LinkingErrorState')
);

export const Portfolio = () => {
  const { portfolioId, errorStatusCode } = useReduxStateForPortfolio();
  const { status, data: portfolio } = usePortfolio({ id: portfolioId });
  // Preload portfolio link settings
  usePortfolioLinkSettings({ portfolioId });

  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  const holdings = portfolio?.holdings ?? [];
  const unmatchedHoldings = portfolio?.link.unmatchedHoldings ?? [];

  const isError = status === 'error' || (status === 'success' && !portfolio);
  const isLinkingStatusCodeError =
    (status === 'error' || status === 'success') &&
    (errorStatusCode === '400' || errorStatusCode === '403');
  const isImporting =
    status === 'success' &&
    portfolio?.link?.status === 'importing' &&
    !isLinkingStatusCodeError;
  const isPopulated =
    status !== 'error' &&
    portfolio &&
    (holdings.length > 0 || unmatchedHoldings.length > 0);
  const isEmpty =
    status === 'success' &&
    portfolio &&
    holdings.length === 0 &&
    unmatchedHoldings.length === 0;

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  if (isLinkingStatusCodeError) {
    return (
      <Track>
        <DocumentHeader title="Portfolio" />
        <OneColumn>
          <LinkingErrorState portfolio={portfolio} />
        </OneColumn>
      </Track>
    );
  }

  if (isError) {
    return (
      <Track>
        <DocumentHeader title="Portfolio" />
        <OneColumn>
          <ErrorState />
        </OneColumn>
      </Track>
    );
  }

  if (isImporting) {
    return (
      <Track>
        <DocumentHeader title={portfolio.name} />
        <OneColumn>
          <ImportingState portfolio={portfolio} />
        </OneColumn>
      </Track>
    );
  }

  if (isEmpty) {
    return (
      <Track>
        <DocumentHeader title={portfolio.name} />
        <OneColumn>
          <ManualPortfolioProvider>
            <EmptyState portfolio={portfolio} />
          </ManualPortfolioProvider>
        </OneColumn>
      </Track>
    );
  }

  if (isPopulated) {
    return (
      <Track>
        <DocumentHeader title={portfolio.name} />
        <OneColumn>
          <ManualPortfolioProvider>
            <CompanyNoteModalProvider>
              <PopulatedState portfolio={portfolio} />
            </CompanyNoteModalProvider>
          </ManualPortfolioProvider>
        </OneColumn>
        <BrazeLogPortfolioVisit portfolio={portfolio} />
      </Track>
    );
  }

  return null;
};
